import axios from 'axios';
import moment from 'moment';
import { KPIKeys, AnalyticsType } from '@/utils/common';

const apiUrl = process.env.VUE_APP_API_URL;

const analyticsOverviewInitialValues = () => ({
  isLoading: true,
  data: null,
  selectedOption: KPIKeys.IMPRESSIONS,
});

const analyticsAdvancedInitialValues = () => ({
  isLoading: true,
  data: null,
  previousAccountsData: null,
  previousCompaniesData: null,
  selectedOption: KPIKeys.IMPRESSIONS,
  selectedServices: [],
  selectedAccounts: [],
  selectedCompanies: [],
});

function getDefaultDateRange() {
  const today = moment();
  if (today.date() === 1) {
    // Если сегодня 1-е число месяца — показываем весь прошлый месяц
    const prevMonth = today.clone().subtract(1, 'month');
    return {
      date_from: prevMonth.startOf('month').format('YYYY-MM-DD'),
      date_to: prevMonth.endOf('month').format('YYYY-MM-DD'),
    };
  } else {
    // В остальных случаях — с 1-го по вчерашний день текущего месяца
    return {
      date_from: today.clone().startOf('month').format('YYYY-MM-DD'),
      date_to: today.clone().subtract(1, 'day').format('YYYY-MM-DD'),
    };
  }
}

const initState = () => {
  return {
    activeSubTab: AnalyticsType.OVERVIEW,
    dateRange: getDefaultDateRange(),
    [AnalyticsType.OVERVIEW]: analyticsOverviewInitialValues(),
    [AnalyticsType.ADVANCED]: analyticsAdvancedInitialValues(),
  };
};

const state = () => initState();

const getters = {};
const mutations = {
  setPreviousAccountsData(state, data) {
    state[AnalyticsType.ADVANCED].previousAccountsData = data;
  },
  setPreviousCompaniesData(state, data) {
    state[AnalyticsType.ADVANCED].previousCompaniesData = data;
  },
  resetPreviousStepData(state) {
    state[AnalyticsType.ADVANCED].previousAccountsData = null;
    state[AnalyticsType.ADVANCED].previousCompaniesData = null;
  },
  resetPreviousCompaniesData(state) {
    state[AnalyticsType.ADVANCED].previousCompaniesData = null;
  },
  changeSelectedServices(state, value) {
    state[AnalyticsType.ADVANCED].selectedServices = value;
  },
  changeSelectedAccounts(state, value) {
    state[AnalyticsType.ADVANCED].selectedAccounts = value;
  },
  changeSelectedCompanies(state, value) {
    state[AnalyticsType.ADVANCED].selectedCompanies = value;
  },
  changeAnalyticsDateRange(state, value) {
    state.dateRange = value;
  },
  changeAnalyticsSelectedOption(state, { value, type }) {
    state[type].selectedOption = value;
  },
  toggleLoading(state, { value, type }) {
    state[type].isLoading = value;
  },
  resetState(state) {
    Object.assign(state, initState());
  },
};
const actions = {
  async getOverviewAnalytics({ commit, state }, businessAccountId) {
    return new Promise((resolve, reject) => {
      commit('toggleLoading', { value: true, type: AnalyticsType.OVERVIEW });

      axios({
        url: `${apiUrl}/business-account/${businessAccountId}/get-full-stats-between-dates`,
        params: { ...state.dateRange },
        method: 'GET',
      })
        .then((resp) => {
          const data = resp.data.data;

          const filteredData = Object.keys(data)
            .filter((key) => Array.isArray(data[key]) && data[key].length > 0)
            .reduce((obj, key) => {
              obj[key] = data[key];
              return obj;
            }, {});

          if (Object.keys(resp.data.data).length) {
            state[AnalyticsType.OVERVIEW].data = filteredData;
            state[AnalyticsType.ADVANCED].data = filteredData;
          }

          resolve(filteredData);
        })
        .catch((err) => {
          state[AnalyticsType.OVERVIEW].data = [];
          reject(err);
        })
        .finally(() => {
          commit('toggleLoading', { value: false, type: AnalyticsType.OVERVIEW });
        });
    });
  },
  async getAdvancedAnalytics({ commit, state }, businessAccountId) {
    const {
      selectedAccounts,
      selectedCompanies,
      selectedServices,
      previousAccountsData,
      previousCompaniesData,
    } = state[AnalyticsType.ADVANCED];

    if (selectedServices.length > 1 || !selectedServices.length) {
      return new Promise((resolve, reject) => {
        const overviewAnalyticsData = state[AnalyticsType.OVERVIEW].data;
        state[AnalyticsType.ADVANCED].data = overviewAnalyticsData;
        resolve(overviewAnalyticsData);
      });
    } else {
      return new Promise((resolve, reject) => {
        commit('toggleLoading', { value: true, type: AnalyticsType.ADVANCED });

        // Проверяем что businessAccountId определен
        if (!businessAccountId) {
          reject(new Error('businessAccountId is required'));
          return;
        }

        // Формируем параметры вручную для точного контроля
        const params = {
          date_from: state.dateRange.date_from,
          date_to: state.dateRange.date_to,
        };

        // Добавляем services в нужном формате
        params['service'] = selectedServices;

        // Добавляем accounts если есть
        if (selectedAccounts && selectedAccounts.length) {
          params['account'] = selectedAccounts;
        }

        // Добавляем companies если есть (обратите внимание на опечатку в примере)
        if (selectedCompanies && selectedCompanies.length) {
          params['campaigns[]'] = selectedCompanies;
        }

        // Специальный обработчик параметров для axios
        const paramsSerializer = (params) => {
          const parts = [];

          for (const key in params) {
            if (Array.isArray(params[key])) {
              params[key].forEach((value) => {
                parts.push(`${key}=${encodeURIComponent(value)}`);
              });
            } else {
              parts.push(`${key}=${encodeURIComponent(params[key])}`);
            }
          }

          return parts.join('&');
        };

        axios({
          url: `${apiUrl}/business-account/${businessAccountId}/get-full-stats-with-params-between-dates`,
          params: params,
          paramsSerializer: paramsSerializer,
          method: 'GET',
        })
          .then((resp) => {
            resolve(resp.data);

            if (Object.keys(resp.data.data).length) {
              state[AnalyticsType.ADVANCED].data = resp.data.data;

              // Не сбрасываем previousCompaniesData безусловно!
              // commit('resetPreviousCompaniesData'); // <-- убрать или вызывать только при смене аккаунта

              if (selectedCompanies.length) {
                // Не обновляем previousCompaniesData, если выбраны кампании
                return;
              } else if (
                selectedAccounts.length &&
                !(previousCompaniesData && previousCompaniesData.length)
              ) {
                commit('setPreviousCompaniesData', resp.data.data);
              } else if (
                selectedServices.length &&
                !(previousAccountsData && previousAccountsData.length)
              ) {
                commit('setPreviousAccountsData', resp.data.data);
              }
            }
          })
          .catch((err) => {
            state[AnalyticsType.ADVANCED].data = [];
            reject(err);
          })
          .finally(() => {
            commit('toggleLoading', { value: false, type: AnalyticsType.ADVANCED });
          });
      });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
